import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import useXHR from '../hooks/useXHR';
import ProtocolAnalyticsDashboard from '../components/Analytics/ProtocolAnalyticsDashboard';
import AdvancedAnalyticsDashboard from '../components/Analytics/AdvancedAnalyticsDashboard';

// Import Book Advertisement
import { MediumBookAd } from '../components/Ads/BookAdvertisements';
import AdCopyGenerator from '../components/Ads/AdCopyGenerator';

// Import Additional Charts
import {
  UserEngagementChart,
  CategoryGrowthChart,
  GeographicDistributionChart,
  ProtocolComplexityRadar,
  ContentTypeBreakdown
} from '../components/Analytics/AdditionalCharts';

const AnalyticsPage = ({ showToast }) => {
  const { user, token } = useAuth();
  const { get } = useXHR(token);
  const [activeView, setActiveView] = useState('advanced');
  const [searchResults, setSearchResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mapResults, setMapResults] = useState([]);
  const [dateRange, setDateRange] = useState('30'); // days

  // Fetch data for analytics using useXHR
  useEffect(() => {
    if (!token) return;
    get('/categories', {
      onSuccess: (data) => setCategories(data || [])
    });
    get(`/map/worldwide?limit=200`, {
      onSuccess: (data) => {
        const results = data?.results || [];
        setMapResults(results);
        setSearchResults(results);
      }
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived stats
  const stats = useMemo(() => {
    const catDist = {};
    const docDist = {};
    const qualityBuckets = { high: 0, medium: 0, low: 0 };
    searchResults.forEach(r => {
      (r.categories || []).forEach(c => { catDist[c] = (catDist[c] || 0) + 1; });
      const dt = r.article_type || 'Unknown';
      docDist[dt] = (docDist[dt] || 0) + 1;
      const q = r.content_quality_score || 0;
      if (q >= 70) qualityBuckets.high++;
      else if (q >= 40) qualityBuckets.medium++;
      else qualityBuckets.low++;
    });
    return { catDist, docDist, qualityBuckets, total: searchResults.length };
  }, [searchResults]);

  if (!token) {
    return (
      <div style={{
        background: 'rgba(253, 224, 71, 0.1)', borderRadius: 16,
        padding: 60, textAlign: 'center', border: '1px solid rgba(253, 224, 71, 0.2)'
      }}>
        <h2 style={{ color: '#0F172A', marginBottom: 10 }}>Login Required</h2>
        <p style={{ color: '#a1a1aa' }}>Please log in to view your analytics.</p>
      </div>
    );
  }

  return (
    <div data-testid="analytics-page" style={{ padding: '20px 0' }}>
      {/* Page Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(245, 158, 11, 0.12))',
        borderRadius: 16, padding: 24, marginBottom: 24,
        border: '1px solid rgba(16, 185, 129, 0.25)'
      }}>
        <h1 style={{
          fontSize: '1.8rem', fontWeight: 800, marginBottom: 8,
          background: 'linear-gradient(135deg, #047857, #0F172A)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
        }}>
          Analytics Hub
        </h1>
        <p style={{ color: '#a1a1aa', fontSize: '0.9rem', margin: '0 0 16px' }}>
          Track your protocol performance, content distribution, and data insights
        </p>

        {/* Controls Row */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* View Toggle */}
          {['advanced', 'protocol'].map(v => (
            <button key={v} data-testid={`analytics-view-${v}`}
              onClick={() => setActiveView(v)}
              style={{
                padding: '8px 18px', borderRadius: 20, border: 'none', cursor: 'pointer',
                background: activeView === v ? 'linear-gradient(135deg, #FDE047, #FDE68A)' : 'rgba(203, 213, 225, 0.8)',
                color: '#0F172A', fontWeight: activeView === v ? 600 : 400, fontSize: '0.85rem'
              }}
            >
              {v === 'advanced' ? 'Advanced Analytics' : 'Protocol Analytics'}
            </button>
          ))}

          {/* Date Range Picker */}
          <div data-testid="analytics-date-range" style={{
            display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto',
            background: '#475569', borderRadius: 10,
            border: '1px solid rgba(252, 211, 77,0.25)', padding: '4px 6px'
          }}>
            {[{ v: '7', l: '7d' }, { v: '14', l: '14d' }, { v: '30', l: '30d' }, { v: '90', l: '90d' }].map(d => (
              <button key={d.v} onClick={() => setDateRange(d.v)}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: dateRange === d.v ? 'rgba(252, 211, 77,0.3)' : 'transparent',
                  color: dateRange === d.v ? '#047857' : '#71717a', fontSize: '0.75rem', fontWeight: 600
                }}
              >{d.l}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div data-testid="analytics-quick-stats" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12, marginBottom: 24
      }}>
        {[
          { label: 'Total Results', value: stats.total, color: '#3b82f6', icon: 'fas fa-database' },
          { label: 'Categories', value: categories.length, color: '#FDE047', icon: 'fas fa-tags' },
          { label: 'High Quality', value: stats.qualityBuckets.high, color: '#10b981', icon: 'fas fa-star' },
          { label: 'Doc Types', value: Object.keys(stats.docDist).length, color: '#f59e0b', icon: 'fas fa-file-alt' }
        ].map(s => (
          <div key={s.label} style={{
            background: `${s.color}12`, border: `1px solid ${s.color}30`, borderRadius: 12, padding: '14px 16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <i className={s.icon} style={{ color: s.color, fontSize: '0.85rem' }} />
              <span style={{ color: '#71717a', fontSize: '0.72rem', fontWeight: 600 }}>{s.label}</span>
            </div>
            <div style={{ color: s.color, fontSize: '1.4rem', fontWeight: 700 }}>{s.value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Main Dashboard View */}
      {activeView === 'advanced' && (
        <AdvancedAnalyticsDashboard searchResults={searchResults} categories={categories} mapResults={mapResults} />
      )}
      {activeView === 'protocol' && (
        <ProtocolAnalyticsDashboard showToast={showToast} />
      )}

      {/* Additional Analytics Charts */}
      <div style={{ marginTop: 25 }}>
        <h3 style={{ color: '#334155', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, fontSize: '1.1rem' }}>
          <i className="fas fa-chart-bar" style={{ color: '#047857' }} />
          Additional Analytics
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 20 }}>
          <UserEngagementChart token={token} days={parseInt(dateRange)} />
          <CategoryGrowthChart token={token} />
          <GeographicDistributionChart token={token} />
          <ProtocolComplexityRadar token={token} />
          <ContentTypeBreakdown token={token} />
        </div>
      </div>

      {/* AI Ad Copy Generator */}
      <div style={{ marginTop: 25 }}>
        <AdCopyGenerator showToast={showToast} />
      </div>

      {/* Book Advertisement */}
      <MediumBookAd variant={4} />
    </div>
  );
};

export default AnalyticsPage;
